import { track } from '@eventbrite/datalayer-library';
import { trackEventFromState } from '@eventbrite/site-analytics';
import { logEvent } from '@eventbrite/statsig';
import isUndefined from 'lodash/isUndefined';
import {
    GA_ENTER_EMAIL_EVENT,
    GA_EVENTBRITE,
    GA_EXISTING_ACCT_LABEL,
    GA_FACEBOOK,
    GA_FB_LOGIN_LOGGED_IN_TO_FB,
    GA_FB_LOGIN_NOT_LOGGED_IN_TO_FB,
    GA_GET_STARTED_EMAIL_ALREADY_CONNECTED,
    GA_HANDLE_ON_ERROR,
    GA_LOGIN_CLICKED,
    GA_LOGIN_SUCCESSFUL,
    GA_LOGIN_SUCCESSONESTEP,
    GA_NEW_ACCT_LABEL,
    GA_PASSWORD_INCORRECT,
    GA_PASSWORD_VIEWED,
    GA_SOFT_ACCT_LABEL,
    HEAP_ONESTEPSUCCESS_LOGIN,
} from '../constants/analytics';
import { trackGeneralAuthAction } from './general';
import { IAnalyticsDimensionsProps } from './interfaces';
import {
    capitalize,
    getAnalyticsCategory,
    getSocialConnectionsKey,
} from './utils';

interface ITrackLoginProps {
    sourceProvider?: string;
    stateFromProvider?: any;
    analytics?: IAnalyticsDimensionsProps;
}

interface ITrackGenericProps {
    errorMessage?: string;
    label?: string;
    analytics?: IAnalyticsDimensionsProps;
}

export const trackLoginClicked = ({
    sourceProvider,
    stateFromProvider,
    analytics,
}: ITrackLoginProps) => {
    if (analytics) {
        trackEventFromState(analytics, {
            category: getAnalyticsCategory(analytics),
            action: GA_LOGIN_CLICKED,
            label: !isUndefined(sourceProvider)
                ? capitalize(sourceProvider)
                : GA_EVENTBRITE,
        });

        let actionFB = '';
        if (sourceProvider === GA_FACEBOOK) {
            actionFB = GA_FB_LOGIN_NOT_LOGGED_IN_TO_FB;
            if (
                stateFromProvider &&
                (stateFromProvider.userID ||
                    stateFromProvider.status === 'not_authorized')
            ) {
                actionFB = GA_FB_LOGIN_LOGGED_IN_TO_FB;
            }

            trackEventFromState(analytics, {
                category: getAnalyticsCategory(analytics),
                action: actionFB,
                label: capitalize(GA_FACEBOOK),
            });
        }
    }
};

/**
 *
 * Track OneStep success events in Heap and Statsig
 */
export const trackLoginSuccessOneStep = ({
    socialProvider,
    analytics,
}: {
    socialProvider: string;
    analytics?: IAnalyticsDimensionsProps;
}) => {
    trackEventFromState(analytics, {
        category: getAnalyticsCategory(analytics),
        action: GA_LOGIN_SUCCESSONESTEP,
        label: !isUndefined(socialProvider)
            ? capitalize(socialProvider)
            : GA_EVENTBRITE,
    });
    track({
        eventName: HEAP_ONESTEPSUCCESS_LOGIN,
        eventData: {
            loginMethod: !isUndefined(socialProvider)
                ? capitalize(socialProvider)
                : GA_EVENTBRITE,
        },
    });
    logEvent(HEAP_ONESTEPSUCCESS_LOGIN, null, {
        loginMethod: !isUndefined(socialProvider)
            ? capitalize(socialProvider)
            : GA_EVENTBRITE,
    });
};

export const trackHandleOnErrorMetric = ({
    errorMessage,
    label = GA_EVENTBRITE,
    analytics,
}: ITrackGenericProps) => {
    if (analytics) {
        trackEventFromState(analytics, {
            category: getAnalyticsCategory(analytics),
            action: GA_HANDLE_ON_ERROR,
            label: errorMessage + '-' + capitalize(label),
        });
    }
};

export const trackPasswordIncorrect = ({
    label = GA_EVENTBRITE,
    analytics,
}: ITrackGenericProps) => {
    if (analytics) {
        trackEventFromState(analytics, {
            category: getAnalyticsCategory(analytics),
            action: GA_PASSWORD_INCORRECT,
            label: !isUndefined(label) ? capitalize(label) : GA_EVENTBRITE,
        });
    }
};
export const trackEnterEmailExistingAccount = ({
    label = GA_EXISTING_ACCT_LABEL,
    analytics,
}: ITrackGenericProps) => {
    if (analytics) {
        trackEventFromState(analytics, {
            category: getAnalyticsCategory(analytics),
            action: GA_ENTER_EMAIL_EVENT,
            label: !isUndefined(label) ? capitalize(label) : GA_EVENTBRITE,
        });
    }
};
export const trackEnterEmailSoftAccount = ({
    label = GA_SOFT_ACCT_LABEL,
    analytics,
}: ITrackGenericProps) => {
    if (analytics) {
        trackEventFromState(analytics, {
            category: getAnalyticsCategory(analytics),
            action: GA_ENTER_EMAIL_EVENT,
            label: !isUndefined(label) ? capitalize(label) : GA_EVENTBRITE,
        });
    }
};

export const trackEnterEmailNewAccount = ({
    label = GA_NEW_ACCT_LABEL,
    analytics,
}: ITrackGenericProps) => {
    if (analytics) {
        trackEventFromState(analytics, {
            category: getAnalyticsCategory(analytics),
            action: GA_ENTER_EMAIL_EVENT,
            label: !isUndefined(label) ? capitalize(label) : GA_EVENTBRITE,
        });
    }
};

export const trackAccountAlreadyConnectedWithSocials = ({
    sourceProvider,
    analytics,
    connectedSocials,
}: any) => {
    if (analytics) {
        const key = getSocialConnectionsKey(connectedSocials);

        trackEventFromState(analytics, {
            category: getAnalyticsCategory(analytics),
            action: GA_GET_STARTED_EMAIL_ALREADY_CONNECTED[key],

            label: !isUndefined(sourceProvider)
                ? capitalize(sourceProvider)
                : GA_EVENTBRITE,
        });
    }
};

export const trackLoginSuccessful = ({
    socialProvider,
    analytics,
    actionCategory,
}: {
    socialProvider?: string;
    analytics?: IAnalyticsDimensionsProps;
    actionCategory?: string;
}) => {
    if (analytics) {
        trackEventFromState(analytics, {
            category: actionCategory ?? getAnalyticsCategory(analytics),
            action: GA_LOGIN_SUCCESSFUL,
            label: !isUndefined(socialProvider)
                ? capitalize(socialProvider)
                : GA_EVENTBRITE,
        });
    }
};

export const trackPasswordViewed = ({
    socialProvider,
    analytics,
}: {
    socialProvider: string;
    analytics?: IAnalyticsDimensionsProps;
}) => {
    trackGeneralAuthAction({
        action: GA_PASSWORD_VIEWED,
        label: !isUndefined(socialProvider)
            ? capitalize(socialProvider)
            : GA_EVENTBRITE,
        analytics,
    });
};
